import { useEffect, useState } from 'react';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import type { ScrapperEventListItem, ScrapperEventStatus } from './types';
import { EVENT_TYPE_LABELS, STATUS_LABELS } from './types';

interface Props {
  event: ScrapperEventListItem | null;
  onClose: () => void;
  onRetry: (id: string) => Promise<void>;
  retrying: string | null;
}

const formatDateTime = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-CL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/Santiago',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const STATUS_TONE: Record<ScrapperEventStatus, { ring: string; bg: string; text: string; label: string; dot: string }> = {
  pending: { ring: 'ring-amber-300/50', bg: 'bg-amber-100', text: 'text-amber-800', label: 'PENDIENTE', dot: 'bg-amber-500' },
  processing: { ring: 'ring-cyan-300/50', bg: 'bg-cyan-100', text: 'text-cyan-800', label: 'PROCESANDO', dot: 'bg-cyan-500' },
  completed: { ring: 'ring-emerald-300/50', bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'COMPLETADO', dot: 'bg-emerald-500' },
  failed: { ring: 'ring-rose-300/50', bg: 'bg-rose-100', text: 'text-rose-800', label: 'FALLIDO', dot: 'bg-rose-500' },
};

export default function ScrapperEventDetailDrawer({ event, onClose, onRetry, retrying }: Props) {
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!event) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [event, onClose]);

  if (!event) return null;

  const tone = STATUS_TONE[event.status];
  const canDownload = Boolean(event.s3Key);

  const handleDownload = async () => {
    if (!event.s3Key) return;
    setDownloading(true);
    try {
      const url = getAdminEndpointUrl('scrapperEvents', 'downloadUrl', { id: event.id });
      const result = await adminHttpClient.get<{ url: string; expiresInSeconds: number; s3Key: string; fileName: string | null }>(url);
      window.open(result.url, '_blank', 'noopener');
    } catch (err) {
      alert(`No se pudo generar la URL de descarga: ${(err as Error).message}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <aside className="fixed right-0 top-0 z-50 flex h-screen w-full max-w-2xl flex-col border-l border-slate-800 bg-slate-950 text-slate-100 shadow-2xl">
        {/* Header */}
        <div className="relative overflow-hidden border-b border-slate-800 px-7 py-5">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
              backgroundSize: '20px 20px',
            }}
          />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${tone.bg} ${tone.text} ring-1 ring-inset ${tone.ring}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                  {tone.label}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{EVENT_TYPE_LABELS[event.eventType]}</span>
              </div>
              <h3 className="mt-3 font-mono text-sm text-slate-400">scrapper_events</h3>
              <code className="mt-0.5 block font-mono text-base text-slate-100">{event.id}</code>
            </div>

            <button onClick={onClose} className="rounded-lg border border-slate-700 p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white">
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 space-y-6 overflow-y-auto px-7 py-6">
          {/* Error block (si aplica) */}
          {event.status === 'failed' && (
            <div className="rounded-xl border border-rose-900/50 bg-gradient-to-br from-rose-950/60 via-slate-950 to-rose-950/30 p-5">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-rose-400">bug_report</span>
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-rose-300">stack trace</p>
              </div>

              {event.errorCode && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-rose-500/15 px-2 py-1 font-mono text-xs font-medium text-rose-300 ring-1 ring-inset ring-rose-500/30">
                    {event.errorCode}
                  </span>
                  {event.failedStep && (
                    <span className="font-mono text-[11px] text-slate-400">
                      @ <span className="text-amber-300">{event.failedStep}</span>
                    </span>
                  )}
                  <span className="ml-auto font-mono text-[11px] text-slate-500">
                    intento <span className="tabular-nums text-slate-300">#{event.attempts}</span>
                  </span>
                </div>
              )}

              {event.errorMessage && (
                <pre className="mt-4 max-h-64 overflow-auto rounded-lg border border-rose-900/40 bg-slate-950/80 p-4 font-mono text-[11px] leading-relaxed text-rose-100/90">
                  <code>{event.errorMessage}</code>
                </pre>
              )}

              {!event.errorMessage && !event.errorCode && (
                <p className="mt-4 font-mono text-xs text-slate-500">— sin detalle de error —</p>
              )}
            </div>
          )}

          {/* Identidad */}
          <Section title="Identidad">
            <Field label="scrapperId" value={event.scrapperId} mono />
            <Field label="status" value={STATUS_LABELS[event.status]} />
            <Field label="eventType" value={EVENT_TYPE_LABELS[event.eventType]} />
            <Field label="attempts" value={String(event.attempts)} mono />
          </Section>

          {/* Comercio + adquirente */}
          <Section title="Comercio · Adquirente">
            <Field label="empresa" value={event.company.businessName ?? '—'} />
            <Field label="taxId" value={event.company.taxId ?? '—'} mono />
            <Field label="companyId" value={event.company.id} mono />
            <Field label="adquirente" value={event.acquirer.name || event.acquirer.slug || '—'} />
            <Field label="slug" value={event.acquirer.slug || '—'} mono />
            <Field label="acquirerId" value={event.acquirer.id} mono />
          </Section>

          {/* Archivo S3 */}
          <Section title="Archivo S3">
            <Field label="bucket" value={event.bucket ?? '—'} mono />
            <Field label="s3Key" value={event.s3Key ?? '—'} mono breakAll />
            <Field label="fileName" value={event.fileName ?? '—'} mono breakAll />
            <Field label="uploadedAt" value={formatDateTime(event.uploadedAt)} mono />
          </Section>

          {/* Timestamps */}
          <Section title="Timeline">
            <Field label="createdAt" value={formatDateTime(event.createdAt)} mono />
            <Field label="lastScrapperAt" value={formatDateTime(event.lastScrapperAt)} mono />
            <Field label="processedAt" value={formatDateTime(event.processedAt)} mono />
          </Section>
        </div>

        {/* Actions */}
        <div className="border-t border-slate-800 bg-slate-950 px-7 py-4">
          <div className="flex items-center justify-between gap-3">
            <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
              {event.status === 'failed' ? 'Reintentar lo deja en estado pending para que el scheduler lo recoja' : 'Vista de solo lectura'}
            </p>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="rounded-lg border border-slate-700 px-3 py-2 font-mono text-xs text-slate-300 transition-colors hover:bg-slate-800">
                Cerrar
              </button>
              {canDownload && (
                <button
                  onClick={() => void handleDownload()}
                  disabled={downloading}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-4 py-2 font-mono text-xs font-medium text-slate-100 transition-colors hover:bg-slate-700 disabled:opacity-50"
                >
                  <span className={`material-symbols-outlined text-[14px] ${downloading ? 'animate-spin' : ''}`}>{downloading ? 'progress_activity' : 'download'}</span>
                  {downloading ? 'Generando URL…' : 'Descargar archivo'}
                </button>
              )}
              {event.status === 'failed' && (
                <button
                  onClick={() => void onRetry(event.id)}
                  disabled={retrying === event.id}
                  className="inline-flex items-center gap-2 rounded-lg bg-rose-500 px-4 py-2 font-mono text-xs font-medium text-white shadow-lg shadow-rose-500/20 transition-all hover:bg-rose-400 disabled:opacity-50"
                >
                  <span className={`material-symbols-outlined text-[14px] ${retrying === event.id ? 'animate-spin' : ''}`}>{retrying === event.id ? 'progress_activity' : 'replay'}</span>
                  {retrying === event.id ? 'Reintentando…' : 'Reintentar evento'}
                </button>
              )}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">{title}</p>
      <dl className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-4 rounded-xl border border-slate-800 bg-slate-900/40 p-4">
        {children}
      </dl>
    </div>
  );
}

function Field({ label, value, mono = false, breakAll = false }: { label: string; value: string; mono?: boolean; breakAll?: boolean }) {
  return (
    <>
      <dt className="font-mono text-[11px] text-slate-500">{label}</dt>
      <dd className={`text-xs ${mono ? 'font-mono' : ''} ${breakAll ? 'break-all' : ''} text-slate-100`}>{value}</dd>
    </>
  );
}
